import { useEffect, useState } from "react";
import { useAuditEvents, useVerifyAuditChain } from "../../../api/queries";
import type { AuditEvent, AuditVerification } from "../../../api/types";
import { EmptyState, LoadingState } from "../../../components/AsyncStates";
import { Button } from "../../../components/Button";
import { DataTable, type DataTableColumn } from "../../../components/DataTable";
import { PageHeader } from "../../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../../components/Paginator";
import { QueryStates } from "../../../components/QueryStates";
import { StatePill, type PillTone } from "../../../components/StatePill";
import { useFleet } from "../../../fleet/FleetContext";
import { useToast } from "../../../toast/ToastContext";
import { AdminGate } from "../AdminGate";
import "./audit.css";

const PAGE_LIMIT = 50;

const OUTCOME_META: Record<string, { label: string; tone: PillTone }> = {
  success: { label: "Success", tone: "positive" },
  allow: { label: "Allowed", tone: "positive" },
  failure: { label: "Failure", tone: "negative" },
  deny: { label: "Denied", tone: "negative" },
};

function OutcomePill({ outcome }: { outcome: string }) {
  const meta = OUTCOME_META[outcome] ?? { label: outcome, tone: "neutral" as const };
  return <StatePill label={meta.label} tone={meta.tone} shape="dot" />;
}

function actorLabel(event: AuditEvent): string {
  if (!event.actorUserId) return "system";
  return event.actorRole ? `${event.actorUserId} (${event.actorRole})` : event.actorUserId;
}

// VerifyResultBanner renders one of THREE states, branching on `status` — never on
// `ok` alone (GH-73 audit round 3/4, MEDIUM-2). Collapsing "unverifiable" (a leading
// run predates a key rotation and could not be cryptographically checked — expected,
// not an incident) into the same red "tamper" alarm as "broken" (a CONFIRMED break) is
// exactly the false-accusation bug this whole mechanism exists to prevent, now at the
// UI layer where an operator actually reads it.
//
// GH-73 audit round 5, MEDIUM-3: the green "chain intact" banner is now the ONLY
// branch reached by an explicit, positive match (`status === "ok" && ok === true`) —
// never the fall-through `else`. Every prior version of this function used `else` as
// the final branch, which meant an unrecognized or missing `status` (version skew, a
// proxy/gateway dropping fields) rendered GREEN — the single worst possible failure
// mode for a tamper-evidence UI, since it actively tells the operator everything is
// fine when the client simply doesn't understand what it was told. `realClient`'s
// parseAuditVerification already normalizes unrecognized statuses to "unverifiable"
// before this component ever sees them, but this function fails closed independently
// of that (defense in depth — a future client wiring, or a test, must not be able to
// reintroduce the bug just by skipping that normalization step).
// Exported for direct testing (LOW-4, GH-73 audit round 5) — this component has no
// hooks/context dependency, so it can be rendered standalone without the fleet/query
// provider stack AuditPage itself needs.
export function VerifyResultBanner({ result }: { result: AuditVerification }) {
  if (result.status === "ok" && result.ok === true) {
    return (
      <p className="audit-verify audit-verify--ok" role="status">
        Chain intact — {result.count.toLocaleString()} event{result.count === 1 ? "" : "s"} verified.
      </p>
    );
  }
  if (result.status === "broken") {
    return (
      <p className="audit-verify audit-verify--broken" role="alert">
        Integrity check FAILED at seq {result.brokenSeq ?? "?"}
        {result.brokenId ? ` (${result.brokenId})` : ""}
        {result.reason ? `: ${result.reason}` : "."} This trail may have been tampered with — investigate before
        trusting it.
      </p>
    );
  }
  if (result.status === "unverifiable") {
    return (
      <p className="audit-verify audit-verify--unverifiable" role="status">
        No tampering confirmed, but {(result.unverifiablePrefixCount ?? 0).toLocaleString()} earlier event
        {result.unverifiablePrefixCount === 1 ? "" : "s"} could not be cryptographically checked — this is
        expected after a key rotation. Events from seq {result.verifiedFromSeq ?? "?"} onward are fully verified. If
        no rotation was expected, investigate.
      </p>
    );
  }
  // Unrecognized or missing status — never reached in practice (parseAuditVerification
  // already normalizes this), but this is the fail-closed fallback if that ever
  // changes: treat it the same as "cannot confirm nothing is wrong", NOT as "ok".
  return (
    <p className="audit-verify audit-verify--unverifiable" role="status">
      Verification result could not be understood. Treat this trail as unverified and investigate.
    </p>
  );
}

const COLUMNS: DataTableColumn<AuditEvent>[] = [
  { key: "time", header: "Time", cell: (event) => new Date(event.occurredAt).toLocaleString(), className: "data-table__secondary" },
  { key: "actor", header: "Actor", cell: (event) => actorLabel(event) },
  { key: "action", header: "Action", cell: (event) => event.action, className: "data-table__mono" },
  {
    key: "resource",
    header: "Resource",
    cell: (event) => event.resource ?? "—",
    className: (event) => (event.resource ? undefined : "data-table__empty-cell"),
  },
  { key: "target", header: "Target", cell: (event) => event.target || "—", className: "data-table__mono" },
  { key: "outcome", header: "Outcome", cell: (event) => <OutcomePill outcome={event.outcome} /> },
];

function AuditTrail({ fleetId }: { fleetId: string }) {
  const { pushToast } = useToast();
  // Cursor-stack pagination lives in the shared hook now (FE-H2) — reset to
  // page 1 whenever the active fleet changes underneath.
  const paginator = usePaginator(fleetId);
  const [verifyResult, setVerifyResult] = useState<AuditVerification | null>(null);

  const { data, isPending, isError, error, refetch } = useAuditEvents(fleetId, {
    cursor: paginator.cursor,
    limit: PAGE_LIMIT,
  });
  const verifyMutation = useVerifyAuditChain(fleetId);

  // A fleet switch invalidates any in-progress verification result from the
  // previous fleet's trail (pagination itself resets via usePaginator above).
  useEffect(() => {
    setVerifyResult(null);
  }, [fleetId]);

  function handleVerify() {
    verifyMutation.mutate(undefined, {
      onSuccess: (result) => {
        setVerifyResult(result);
        // Three-way, matching VerifyResultBanner (GH-73 round 4, MEDIUM-2): an
        // "unverifiable" result is neither a success toast nor an error toast — it is
        // its own neutral/warning state, and must never read like "Integrity check
        // failed."
        const toastByStatus = {
          ok: {
            title: "Chain intact.",
            description: `${result.count.toLocaleString()} events verified.`,
            tone: "success" as const,
            durationMs: 5000,
          },
          unverifiable: {
            title: "Some events could not be verified.",
            description: `${(result.unverifiablePrefixCount ?? 0).toLocaleString()} leading events predate a key rotation and were not cryptographically checked; no tampering was confirmed.`,
            // "info", not "error" — a toast tone of "warning" does not exist
            // (ToastTone is success | error | info); "unverifiable" is explicitly
            // NOT an error state, so it must not visually alarm like one.
            tone: "info" as const,
            durationMs: 0,
          },
          broken: {
            title: "Integrity check failed.",
            description: `Break at seq ${result.brokenSeq ?? "?"}.`,
            tone: "error" as const,
            durationMs: 0,
          },
        } as const;
        // GH-73 audit round 5, MEDIUM-3: an unrecognized `status` (version skew, a
        // proxy dropping fields) must not produce `toastByStatus[result.status] ===
        // undefined` and crash `pushToast` — fall back to the neutral "unverifiable"
        // toast, matching VerifyResultBanner's own fail-closed fallback.
        pushToast(toastByStatus[result.status] ?? toastByStatus.unverifiable);
      },
      onError: (err) => {
        pushToast({
          title: "Couldn't verify the chain",
          description: err instanceof Error ? err.message : "Try again in a moment.",
          tone: "error",
        });
      },
    });
  }

  const verifyButton = (
    <Button variant="secondary" onClick={handleVerify} disabled={verifyMutation.isPending}>
      {verifyMutation.isPending ? "Verifying…" : "Verify chain integrity"}
    </Button>
  );

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Every security-relevant action taken in this fleet — auth, secrets, deploys, exec sessions — in an immutable, tamper-evident trail."
        actions={verifyButton}
      />

      {verifyResult && <VerifyResultBanner result={verifyResult} />}

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading audit trail…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error ? error.message : "Couldn't load the audit trail. Check your connection and try again."
        }
        isEmpty={Boolean(data && data.items.length === 0)}
        empty={
          <EmptyState title="No audit events yet" description="Actions taken in this fleet will show up here as they happen." />
        }
      >
        {data && (
          <>
            <DataTable columns={COLUMNS} rows={data.items} rowKey={(event) => event.id} />
            <PaginatorControls
              itemCount={data.items.length}
              nextCursor={data.nextCursor}
              paginator={paginator}
              noun="events"
            />
          </>
        )}
      </QueryStates>
    </>
  );
}

export function AuditPage() {
  const { currentId, isLoading: fleetLoading } = useFleet();

  return (
    <AdminGate>
      {fleetLoading ? <LoadingState label="Loading fleet…" /> : <AuditTrail fleetId={currentId} />}
    </AdminGate>
  );
}
